import { useId } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { SUPPORTED_LOCALES, type Locale } from '../i18n/messages';

/**
 * Language selector for the shell header. A native <select> keeps it
 * keyboard- and screen-reader-friendly and gives a 44px target on phones.
 * A failed remote persist (users.locale) is surfaced next to the control;
 * the in-memory locale still switches immediately.
 */
export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { locale, setLocale, t, persistError } = useI18n();
  const id = useId();

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <label htmlFor={id} className="sr-only">
        {t('lang.label')}
      </label>
      <select
        id={id}
        value={locale}
        onChange={(event) => setLocale(event.target.value as Locale)}
        className="min-h-11 md:min-h-8 rounded-md border border-border bg-surface px-2 text-xs font-medium text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {SUPPORTED_LOCALES.map((code) => (
          <option key={code} value={code}>
            {t(`lang.${code}` as 'lang.en' | 'lang.es')}
          </option>
        ))}
      </select>
      {persistError && (
        <span
          role="status"
          className="text-xs text-warning"
          title={persistError}
        >
          {t('lang.persist_failed')}
        </span>
      )}
    </div>
  );
}
